import { useCallback } from 'preact/hooks';

import { type DecodedToken, Roles } from '../api/types';
import { useAppState, useAppStateRaw } from '../stores/app-store/app-store';

type FeatureLevel = 'kassir' | 'test';

export const checkRole = (parsedToken: DecodedToken | null, role: Roles) => {
  if (!parsedToken?.authorities) return false;

  // Allow all features for development
  if (import.meta.env.DEV) return true;

  return parsedToken.authorities.includes(role);
};

export function useFeatureFlag() {
  const { decodedToken, startAppArgs } = useAppState();

  const isKassir = checkRole(decodedToken, Roles.KASSIR);
  const isTestUser = isKassir || checkRole(decodedToken, Roles.TEST);
  const isDev =
    checkRole(decodedToken, Roles.DEV) ||
    !!startAppArgs.labels?.includes('dev') ||
    checkRole(decodedToken, Roles.KASSIR) ||
    checkRole(decodedToken, Roles.TEST);

  const hide = useCallback(
    (level: FeatureLevel) => {
      switch (level) {
        case 'kassir':
          return isKassir;
        case 'test':
          return isKassir || isTestUser;
      }
    },
    [isKassir, isTestUser]
  );

  return { hide, isKassir, isTestUser, isDev };
}

export const getFeatureFlag = () => {
  const { decodedToken, startAppArgs } = useAppStateRaw.getState();

  return {
    isKassir: checkRole(decodedToken, Roles.KASSIR),
    isTestUser: decodedToken ? decodedToken.authorities?.includes(Roles.TEST) : false,
    isDev:
      checkRole(decodedToken, Roles.DEV) ||
      !!startAppArgs.labels?.includes('dev') ||
      checkRole(decodedToken, Roles.KASSIR) ||
      checkRole(decodedToken, Roles.TEST),
  };
};
